import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { videosApi, watchStatusApi, watchLimitApi } from '../services/api';
import VideoPlayer from '../components/video/VideoPlayer';
import type { Video, WatchLimitStatus } from '../types';
export default function VideoPlayerPage() {
  const { videoId } = useParams<{ videoId: string }>();
  const navigate = useNavigate();

  const [video, setVideo] = useState<Video | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markedWatched, setMarkedWatched] = useState(false);
  const [theatreMode, setTheatreMode] = useState(false);
  const [watchLimit, setWatchLimit] = useState<WatchLimitStatus | null>(null);
  const [allowedToWatch, setAllowedToWatch] = useState(false);
  const [activatingDay, setActivatingDay] = useState(false);
  const [confirmDialogType, setConfirmDialogType] = useState<'weekly' | 'extra' | null>(null);
  const toggleTheatre = useCallback(() => setTheatreMode(v => !v), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT') return;
      if (e.key === 't' || e.key === 'T') toggleTheatre();
      if (e.key === 'Escape') setTheatreMode(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleTheatre]);

  useEffect(() => {
    const loadVideoAndCheckLimit = async () => {
      if (!videoId) return;

      try {
        setLoading(true);
        const [videoData, limitStatus] = await Promise.all([
          videosApi.getById(videoId),
          watchLimitApi.getStatus(),
        ]);
        setVideo(videoData);
        setWatchLimit(limitStatus);

        if (limitStatus.isWatchDayToday) {
          // Once playback starts during an active watch day, allow finishing even past 24:00
          setAllowedToWatch(true);
          try {
            await watchStatusApi.markWatched({ videoId, percentage: 100 });
            setMarkedWatched(true);
          } catch (err) {
            console.error('Failed to mark as watched:', err);
          }
        } else if (!limitStatus.weeklyDayUsed) {
          setConfirmDialogType('weekly');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load video');
      } finally {
        setLoading(false);
      }
    };

    loadVideoAndCheckLimit();
  }, [videoId]);

  const handleConfirmActivate = async (type: 'weekly' | 'extra') => {
    if (!videoId) return;
    try {
      setActivatingDay(true);
      const updated = await watchLimitApi.activate(type);
      setWatchLimit(updated);
      setConfirmDialogType(null);
      if (updated.isWatchDayToday) {
        setAllowedToWatch(true);
        try {
          await watchStatusApi.markWatched({ videoId, percentage: 100 });
          setMarkedWatched(true);
        } catch (err) {
          console.error('Failed to mark as watched:', err);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to activate watch day');
    } finally {
      setActivatingDay(false);
    }
  };

  const handleManualMarkWatched = async () => {
    try {
      await watchStatusApi.markWatched({ videoId: videoId!, percentage: 100 });
      setMarkedWatched(true);
    } catch (err) {
      console.error('Failed to mark as watched:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !video) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{ mb: 2 }}
        >
          Back to Videos
        </Button>
        <Alert severity="error">{error || 'Video not found'}</Alert>
      </Box>
    );
  }
  if (!allowedToWatch) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{ mb: 2 }}
        >
          Back to Videos
        </Button>

        {watchLimit && watchLimit.weeklyDayUsed && !watchLimit.isWatchDayToday && (
          <Paper sx={{ p: 6, textAlign: 'center', mb: 4 }}>
            <EventBusyIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              You already had your watch day this week
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto', mb: 3 }}>
              Your weekly watch day (Monday–Sunday) was used on{' '}
              <strong>{watchLimit.weeklyDayDate}</strong>. Your next regular watch day will be available starting Monday.
            </Typography>

            {watchLimit.extraDaysRemaining > 0 ? (
              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<PlayCircleOutlineIcon />}
                  onClick={() => setConfirmDialogType('extra')}
                  disabled={activatingDay}
                >
                  Use Extra Watch Day ({watchLimit.extraDaysRemaining} of {watchLimit.extraDaysLimit} remaining this month)
                </Button>
              </Box>
            ) : (
              <Alert severity="warning" sx={{ maxWidth: 520, mx: 'auto', mt: 2, textAlign: 'left' }}>
                Both of your extra watch days ({watchLimit.extraDaysLimit}/{watchLimit.extraDaysLimit}) have already been used up for this month.
              </Alert>
            )}
          </Paper>
        )}

        <Dialog open={confirmDialogType !== null} onClose={() => navigate('/')} maxWidth="sm" fullWidth>
          <DialogTitle>
            {confirmDialogType === 'extra'
              ? 'Use an Extra Watch Day?'
              : 'Start Your Weekly Watch Day?'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirmDialogType === 'extra' ? (
                <>
                  Do you want to activate one of your monthly extra watch days for today to watch{' '}
                  <strong>{video.title}</strong>? You will be able to watch videos until <strong>24:00</strong>.
                  <br />
                  <br />
                  After activating, you will have{' '}
                  <strong>{Math.max(0, (watchLimit?.extraDaysRemaining ?? 1) - 1)}</strong> of{' '}
                  <strong>{watchLimit?.extraDaysLimit ?? 2}</strong> extra watch days remaining this month.
                </>
              ) : (
                <>
                  You get one YouTube day per week (Monday–Sunday). Do you want to start your watch day now and watch{' '}
                  <strong>{video.title}</strong>?
                  <br />
                  <br />
                  You will be able to watch videos until <strong>24:00</strong> tonight (if you start a video before 24:00, you can still finish watching it).
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => navigate('/')} disabled={activatingDay}>
              Cancel
            </Button>
            <Button
              onClick={() => confirmDialogType && handleConfirmActivate(confirmDialogType)}
              variant="contained"
              color="primary"
              disabled={activatingDay}
              autoFocus
            >
              {activatingDay
                ? 'Starting...'
                : confirmDialogType === 'extra'
                ? 'Use Extra Watch Day'
                : 'Start Watch Day'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <>
      {/* Theatre mode overlay */}
      {theatreMode && (
        <Box
          sx={{
            position: 'fixed',
            top: 64, // below AppBar (~64px on desktop)
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'black',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setTheatreMode(false); }}
        >
          <Box sx={{ position: 'relative', width: '100%', maxWidth: 'calc((100vh - 64px) * 16 / 9)', mx: 'auto' }}>
            {/* Exit button */}
            <Tooltip title="Exit theatre mode (T)">
              <IconButton
                onClick={() => setTheatreMode(false)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  color: 'white',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                }}
              >
                <FullscreenExitIcon />
              </IconButton>
            </Tooltip>
            <VideoPlayer videoId={videoId!} />
          </Box>
        </Box>
      )}

      <Box>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{ mb: 2 }}
        >
          Back to Videos
        </Button>

        {/* Video Player */}
        <Paper sx={{ mb: 3, overflow: 'hidden', maxWidth: 800, position: 'relative' }}>
          <VideoPlayer videoId={videoId!} />
          <Tooltip title="Theatre mode (T)">
            <IconButton
              onClick={toggleTheatre}
              size="small"
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                color: 'white',
                bgcolor: 'rgba(0,0,0,0.5)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
              }}
            >
              <AspectRatioIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Paper>

        {/* Video Info */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" gutterBottom>
                {video.title}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                <Typography variant="body1" color="text.secondary">
                  {video.channelTitle}
                </Typography>
                {video.channelRating && (
                  <Chip
                    label={`⭐ ${video.channelRating}`}
                    size="small"
                    color="primary"
                  />
                )}
              </Box>
            </Box>

            <Button
              variant={markedWatched ? 'outlined' : 'contained'}
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={handleManualMarkWatched}
              disabled={markedWatched}
            >
              {markedWatched ? 'Marked as Watched' : 'Mark as Watched'}
            </Button>
          </Box>

          <Typography variant="body2" color="text.secondary" paragraph>
            Published: {new Date(video.publishedAt).toLocaleDateString()}
          </Typography>

          {video.description && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {video.description}
              </Typography>
            </Box>
          )}
        </Paper>

        {markedWatched && (
          <Alert severity="success" sx={{ mt: 2 }}>
            This video has been marked as watched!
          </Alert>
        )}
      </Box>
    </>
  );
}
